import React, { useState, useRef } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Chip, Typography, Box, Fade,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Stack, Alert, CircularProgress, Tooltip, Snackbar, TextField,
  LinearProgress, Badge, Divider,
} from '@mui/material';
import { Visibility, EmojiEvents, DeleteOutline, WarningAmberRounded } from '@mui/icons-material';
import { format } from 'date-fns';
import axios from 'axios';
import { BASE_URL, apiCall } from '../api/apiClient';
import EventDetailDialog from './EventDetailDialog';

// ─── Shared Design Tokens ─────────────────────────────────────────────────────
const BG        = '#f4f7f5';
const SURFACE   = '#ffffff';
const BORDER    = '#d6e4de';
const ACCENT    = '#1e6b45';
const ACCENT_BG = '#e6f2ec';
const TEXT_PRI  = '#0f1f14';
const TEXT_SEC  = '#4b6358';

// ─── Shared input style ───────────────────────────────────────────────────────
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    bgcolor: SURFACE,
    color: TEXT_PRI,
    '& fieldset': { borderColor: BORDER },
    '&:hover fieldset': { borderColor: ACCENT },
    '&.Mui-focused fieldset': { borderColor: ACCENT },
  },
  '& .MuiInputLabel-root': { color: TEXT_SEC },
  '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
};

const statusColor = { open: 'success', closed: 'error', upcoming: 'info', completed: 'default' };

const CONFIRM_DIALOGS = [
  {
    title: 'Première confirmation',
    body: (name) => <>Vous êtes sur le point de supprimer l'événement <b>{name}</b>. Êtes-vous certain de vouloir continuer ?</>,
    action: 'Oui, continuer',
    step: 1,
  },
  {
    title: 'Deuxième confirmation',
    body: (name) => <>Cette action est <b>irréversible</b>. L'événement <b>{name}</b> et toutes ses données seront définitivement supprimés. Confirmez-vous ?</>,
    action: 'Oui, je confirme',
    step: 2,
  },
  {
    title: 'Confirmation finale',
    body: (name) => <>Dernière étape. Tapez <b>Je confirme</b> ci-dessous pour supprimer définitivement <b>{name}</b>.</>,
    action: 'Supprimer définitivement',
    step: 3,
    requiresText: true,
  },
];

const getCurrentUserId = () => {
  try {
    const user = localStorage.getItem('user');
    if (user) {
      const parsed = JSON.parse(user);
      return parsed._id || parsed.id || null;
    }
  } catch { /* ignore */ }
  return null;
};

const EventsTable = ({ events, onRefresh }) => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [deleteEvent, setDeleteEvent]     = useState(null);
  const [deleteStep, setDeleteStep]       = useState(0);
  const [confirmText, setConfirmText]     = useState('');
  const [deleting, setDeleting]           = useState(false);
  const [deleteError, setDeleteError]     = useState('');
  const clickCounts = useRef({});
  const clickTimers = useRef({});
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg]   = useState('');

  const currentUserId = getCurrentUserId();

  const isOwner = (ev) => {
    if (!currentUserId) return false;
    const createdBy = ev.createdBy;
    if (!createdBy) return false;
    return (
      createdBy === currentUserId ||
      createdBy?._id === currentUserId ||
      createdBy?.id  === currentUserId
    );
  };

  const handleDeleteClick = (ev) => {
    const id = ev._id;
    clickCounts.current[id] = (clickCounts.current[id] || 0) + 1;
    const count = clickCounts.current[id];
    clearTimeout(clickTimers.current[id]);
    clickTimers.current[id] = setTimeout(() => { clickCounts.current[id] = 0; }, 3000);

    if (count === 1) {
      setToastMsg('⚠️ Cliquez 3 fois sur supprimer pour déclencher la suppression.');
      setToastOpen(true);
    } else if (count === 2) {
      setToastMsg('⚠️ Encore 1 clic pour ouvrir la confirmation de suppression.');
      setToastOpen(true);
    } else if (count >= 3) {
      clickCounts.current[id] = 0;
      clearTimeout(clickTimers.current[id]);
      setDeleteEvent(ev);
      setDeleteStep(1);
      setConfirmText('');
      setDeleteError('');
    }
  };

  const handleNextStep = () => {
    if (deleteStep < 3) { setDeleteStep((s) => s + 1); setConfirmText(''); setDeleteError(''); }
  };

  const handleCancelDelete = () => {
    if (deleting) return;
    setDeleteEvent(null); setDeleteStep(0); setConfirmText(''); setDeleteError('');
  };

  const handleConfirmDelete = async () => {
    if (!deleteEvent) return;
    setDeleting(true); setDeleteError('');
    try {
      await apiCall((headers) => axios.delete(`${BASE_URL}/events/${deleteEvent._id}`, { headers }));
      setDeleteEvent(null); setDeleteStep(0); setConfirmText('');
      onRefresh?.();
    } catch (e) {
      setDeleteError(e.response?.data?.message || 'Échec de la suppression.');
    } finally {
      setDeleting(false);
    }
  };

  const step3Valid = confirmText.trim().toLowerCase() === 'je confirme';
  const dialogCfg  = deleteStep >= 1 ? CONFIRM_DIALOGS[deleteStep - 1] : null;

  if (!events.length) return (
    <Box sx={{ py: 8, textAlign: 'center', bgcolor: BG, borderRadius: 2 }}>
      <EmojiEvents sx={{ fontSize: 56, color: BORDER, mb: 1 }} />
      <Typography sx={{ color: TEXT_SEC }}>Aucun événement dans cette catégorie</Typography>
    </Box>
  );

  return (
    <>
      {/* ── Table ── */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ borderRadius: '12px', border: `1px solid ${BORDER}`, bgcolor: SURFACE, overflow: 'hidden' }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {['Nom', 'Dates', 'Type', 'Lieu', 'Statut', 'Actions'].map((label) => (
                <TableCell
                  key={label}
                  align={label === 'Actions' ? 'center' : 'left'}
                  sx={{
                    color: TEXT_SEC, fontWeight: 700, fontSize: '0.68rem',
                    textTransform: 'uppercase', letterSpacing: '0.08em',
                    borderBottom: `1px solid ${BORDER}`, py: 1.5, bgcolor: BG,
                  }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((ev, i) => {
              const canDelete  = isOwner(ev);
              const clickCount = clickCounts.current[ev._id] || 0;

              return (
                <Fade in key={ev._id} timeout={300 + i * 80}>
                  <TableRow
                    hover
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: ACCENT_BG }, '& td': { borderColor: BORDER } }}
                  >
                    {/* Nom */}
                    <TableCell>
                      <Typography variant="body2" fontWeight="600" sx={{ color: TEXT_PRI }}>
                        {ev.name}
                      </Typography>
                    </TableCell>

                    {/* Dates */}
                    <TableCell>
                      <Typography variant="body2" sx={{ color: TEXT_PRI }}>
                        {ev.eventStartDate ? format(new Date(ev.eventStartDate), 'dd MMM yyyy') : '—'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: TEXT_SEC }}>
                        → {ev.eventFinishDate ? format(new Date(ev.eventFinishDate), 'dd MMM yyyy') : '—'}
                      </Typography>
                    </TableCell>

                    {/* Type */}
                    <TableCell>
                      <Chip
                        label={ev.type}
                        size="small"
                        sx={{ bgcolor: ACCENT_BG, color: ACCENT, border: `1px solid ${BORDER}`, fontWeight: 600, fontSize: '0.72rem' }}
                      />
                    </TableCell>

                    {/* Lieu */}
                    <TableCell>
                      {ev.location?.map((l, j) => (
                        <Typography key={j} variant="body2" sx={{ color: TEXT_PRI }}>{l.city}, {l.country}</Typography>
                      ))}
                    </TableCell>

                    {/* Statut */}
                    <TableCell>
                      <Chip
                        label={ev.status}
                        color={statusColor[ev.status] || 'default'}
                        size="small"
                        sx={{ fontWeight: 600, fontSize: '0.72rem' }}
                      />
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="center">
                      <Stack direction="row" justifyContent="center" spacing={0.5}>
                        <Tooltip title="Voir les détails">
                          <IconButton
                            size="small"
                            onClick={() => setSelectedEvent(ev)}
                            sx={{ color: ACCENT, bgcolor: ACCENT_BG, borderRadius: '8px', '&:hover': { bgcolor: BORDER } }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={
                          !canDelete
                            ? "Seul le créateur de l'événement peut le supprimer"
                            : 'Cliquez 3 fois pour supprimer'
                        }>
                          <span>
                            <Badge
                              badgeContent={clickCount > 0 ? `${clickCount}/3` : null}
                              color="error"
                              sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 24, px: 0.5 } }}
                            >
                              <IconButton
                                size="small"
                                disabled={!canDelete}
                                onClick={() => handleDeleteClick(ev)}
                                sx={{
                                  color: '#c62828', bgcolor: '#fdecea', borderRadius: '8px',
                                  opacity: canDelete ? 1 : 0.35,
                                  '&:hover': { bgcolor: '#f9cbc8' },
                                }}
                              >
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Badge>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                </Fade>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── View detail dialog ── */}
      <EventDetailDialog
        open={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        event={selectedEvent}
        onRefresh={() => onRefresh()}
      />

      {/* ── Multi-step delete dialog ── */}
      {dialogCfg && (
        <Dialog
          open={deleteStep >= 1}
          onClose={handleCancelDelete}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px', overflow: 'hidden',
              border: `1px solid ${BORDER}`, bgcolor: SURFACE,
              boxShadow: '0 8px 32px rgba(30,107,69,0.10)',
            },
          }}
        >
          <LinearProgress
            variant="determinate"
            value={(deleteStep / 3) * 100}
            color="error"
            sx={{ height: 4 }}
          />

          <DialogTitle sx={{ pb: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                <WarningAmberRounded color="error" />
                <Typography variant="h6" fontWeight="bold" sx={{ color: TEXT_PRI }}>
                  {dialogCfg.title}
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: TEXT_SEC, fontWeight: 700 }}>
                Étape {deleteStep} / 3
              </Typography>
            </Stack>
          </DialogTitle>

          <Divider sx={{ borderColor: BORDER }} />

          <DialogContent sx={{ bgcolor: BG, pt: 2 }}>
            {deleteError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{deleteError}</Alert>
            )}
            <Typography variant="body2" sx={{ color: TEXT_SEC, mb: dialogCfg.requiresText ? 2 : 0 }}>
              {dialogCfg.body(deleteEvent?.name)}
            </Typography>

            {dialogCfg.requiresText && (
              <TextField
                fullWidth size="small"
                label='Tapez "Je confirme"'
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                error={confirmText.length > 0 && !step3Valid}
                helperText={confirmText.length > 0 && !step3Valid ? 'Veuillez écrire exactement "Je confirme"' : ' '}
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter' && step3Valid && !deleting) handleConfirmDelete(); }}
                sx={inputSx}
              />
            )}
          </DialogContent>

          <Divider sx={{ borderColor: BORDER }} />
          <DialogActions sx={{ px: 3, py: 2, gap: 1, bgcolor: SURFACE }}>
            <Button
              onClick={handleCancelDelete}
              disabled={deleting}
              sx={{ color: TEXT_SEC, borderRadius: '10px', textTransform: 'none' }}
            >
              Annuler
            </Button>

            {deleteStep < 3 && (
              <Button
                onClick={handleNextStep}
                variant="contained"
                color="error"
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
              >
                {dialogCfg.action}
              </Button>
            )}

            {deleteStep === 3 && (
              <Button
                onClick={handleConfirmDelete}
                disabled={deleting || !step3Valid}
                variant="contained"
                color="error"
                startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutline />}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
              >
                {deleting ? 'Suppression…' : dialogCfg.action}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}

      {/* ── Toast ── */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={2500}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity="warning"
          variant="filled"
          sx={{ width: '100%', borderRadius: '10px' }}
        >
          {toastMsg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EventsTable;